import { z } from 'zod';

/** How often a host reports its health (IMM-1450): every minute, and at once when its throttle state changes. */
export const FLEET_HEALTH_INTERVAL_MS = 60_000;
/** A reading dated further ahead of the backend's clock than this is dropped: it would stay "latest" until then. */
export const FLEET_HEALTH_MAX_SKEW_MS = 5 * 60_000;
/** The disk and memory warning line, inclusive, on the floored whole percent (PRD Detailed Business Rules). */
export const FLEET_HEALTH_WARN_PERCENT = 90;

/** Why a machine stopped claiming. `thermal-pressure`: a Mac at "serious" or worse, which reports no degrees. */
export const FLEET_THROTTLE_REASONS = ['temperature', 'thermal-pressure', 'load', 'memory-pressure'] as const;
export type FleetThrottleReason = (typeof FLEET_THROTTLE_REASONS)[number];

/** macOS's thermal-pressure levels; `null` on Linux, which reports degrees instead. */
export const FLEET_THERMAL_PRESSURES = ['nominal', 'fair', 'serious', 'critical'] as const;

const UsedOfTotalSchema = z.object({
  usedBytes: z.number().int().nonnegative(),
  totalBytes: z.number().int().nonnegative(),
});
export type FleetUsedOfTotal = z.infer<typeof UsedOfTotalSchema>;

/** A limit and the level the machine must fall under before it claims again. */
const FleetLimitSchema = z.object({ throttleAt: z.number().nonnegative(), resumeBelow: z.number().nonnegative() });

/** The limits the machine throttles on — its own override where one is set, else the defaults below. */
export const FleetHostLimitsSchema = z.object({
  temperatureC: FleetLimitSchema,
  loadPerCore: FleetLimitSchema,
  memoryPressurePercent: FleetLimitSchema,
});
export type FleetHostLimits = z.infer<typeof FleetHostLimitsSchema>;

/** PRD Detailed Business Rules / Resolved Decision 13. A host may override each per machine (IMM-1450.8). */
export const FLEET_DEFAULT_HOST_LIMITS: FleetHostLimits = {
  temperatureC: { throttleAt: 85, resumeBelow: 75 },
  loadPerCore: { throttleAt: 1.5, resumeBelow: 1.0 },
  memoryPressurePercent: { throttleAt: 10, resumeBelow: 2 },
};

export const FleetHostThrottleSchema = z.object({
  reason: z.enum(FLEET_THROTTLE_REASONS),
  /** The figure holding the machine back as last read: °C, load per core, or pressure %. `null` for thermal-pressure. */
  value: z.number().nonnegative().nullable(),
});
export type FleetHostThrottle = z.infer<typeof FleetHostThrottleSchema>;

/** One reading. Every figure is `null` when the machine cannot read it; the rest still count (AC 3). */
export const FleetHostHealthSchema = z.object({
  measuredAt: z.iso.datetime(),
  /** The volume fleet-agent's checkouts live on. */
  disk: UsedOfTotalSchema.nullable(),
  memory: UsedOfTotalSchema.nullable(),
  swap: UsedOfTotalSchema.nullable(),
  /** Share of time tasks stalled on memory, 0–100 (Linux PSI `some avg10`; macOS mapped, IMM-1450.8). */
  memoryPressurePercent: z.number().min(0).max(100).nullable(),
  /** The hottest CPU package sensor. */
  cpuTemperatureC: z.number().min(-50).max(150).nullable(),
  thermalPressure: z.enum(FLEET_THERMAL_PRESSURES).nullable(),
  load1m: z.number().nonnegative().nullable(),
  cpuCores: z.number().int().positive().max(1024).nullable(),
  limits: FleetHostLimitsSchema,
  /** `null` while the machine claims work. */
  throttle: FleetHostThrottleSchema.nullable(),
});
export type FleetHostHealth = z.infer<typeof FleetHostHealthSchema>;

/** The most points one series returns; the bucket widens until the span fits. */
export const FLEET_HEALTH_MAX_POINTS = 400;
export const FLEET_HEALTH_BUCKETS_MS = [
  60_000, 300_000, 900_000, 3_600_000, 10_800_000, 21_600_000, 86_400_000, 604_800_000,
] as const;

/** The smallest bucket that keeps `spanMs` within FLEET_HEALTH_MAX_POINTS, else the widest. */
export function fleetHealthBucketMs(spanMs: number): number {
  return FLEET_HEALTH_BUCKETS_MS.find((bucket) => spanMs / bucket <= FLEET_HEALTH_MAX_POINTS) ?? 604_800_000;
}

export const FleetHostHealthSeriesQuerySchema = z
  .object({
    /** Inclusive. */
    from: z.iso.datetime(),
    /** Exclusive. */
    to: z.iso.datetime(),
  })
  .refine((query) => Date.parse(query.from) < Date.parse(query.to), {
    message: 'from must be before to',
    path: ['to'],
  });

/** One bucket: each figure's MAXIMUM over its readings, `null` when none reported it. */
export const FleetHostHealthPointSchema = z.object({
  /** The bucket's start (UTC-aligned). */
  at: z.iso.datetime(),
  diskRatio: z.number().nonnegative().nullable(),
  memoryRatio: z.number().nonnegative().nullable(),
  swapRatio: z.number().nonnegative().nullable(),
  cpuTemperatureC: z.number().nullable(),
  loadPerCore: z.number().nonnegative().nullable(),
  memoryPressurePercent: z.number().nonnegative().nullable(),
});
export type FleetHostHealthPoint = z.infer<typeof FleetHostHealthPointSchema>;

export const FleetHostHealthSeriesResponseSchema = z.object({
  data: z.object({
    bucketMs: z.number().int().positive(),
    /** Ascending by `at`; empty buckets omitted. */
    points: z.array(FleetHostHealthPointSchema),
    /** The newest reading inside the range, `null` when it has none. */
    latest: FleetHostHealthSchema.nullable(),
  }),
});
export type FleetHostHealthSeries = z.infer<typeof FleetHostHealthSeriesResponseSchema>['data'];
